import { getDataAPI, postDataAPI } from './fetchData';

// Cache configuration
const CACHE_KEY = 'dizee_preset_prompts_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

// Memory cache for current session
let memoryCache = {
  data: null,
  timestamp: null,
  userId: null
};

// Helper function to get current user ID from token
const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('dizeeToken');
    if (!token) return null;
    
    // Parse JWT token to get user ID (basic parsing)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id || payload.userId || payload.user_id;
  } catch (error) {
    return null;
  }
};

// Helper function to check if cache is valid
const isCacheValid = (cacheData, userId) => {
  if (!cacheData || !cacheData.timestamp || !cacheData.data) {
    return false;
  }
  
  // Check if cache is for the same user
  if (cacheData.userId !== userId) {
    return false;
  }
  
  // Check if cache is not expired
  const now = Date.now();
  return (now - cacheData.timestamp) < CACHE_DURATION;
};

// Helper function to save to cache
const saveToCache = (data, userId) => {
  const cacheData = {
    data,
    timestamp: Date.now(),
    userId
  };
  
  // Save to memory cache
  memoryCache = cacheData;
  
  // Also save to global window object for synchronous access
  window.dizeePresetPromptsCache = cacheData;
  
  // Save to localStorage cache
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to save preset prompts to localStorage cache:', error);
  }
};

// Helper function to load from cache
const loadFromCache = (userId) => {
  // First check memory cache
  if (isCacheValid(memoryCache, userId)) {
    console.log('📋 Loading preset prompts from memory cache');
    return memoryCache.data;
  }
  
  // Then check localStorage cache
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const cacheData = JSON.parse(cached);
      if (isCacheValid(cacheData, userId)) {
        console.log('📋 Loading preset prompts from localStorage cache');
        // Update memory cache
        memoryCache = cacheData;
        return cacheData.data;
      }
    }
  } catch (error) {
    console.warn('Failed to load preset prompts from localStorage cache:', error);
  }
  
  return null;
};

export const presetPromptsApi = {
  // Synchronously load cached preset prompts (for immediate UI rendering)
  getCachedPresetPrompts: () => {
    const userId = getCurrentUserId();
    if (userId) {
      return loadFromCache(userId) || [];
    }
    return [];
  },

  // Get all preset prompts with user preferences (with caching)
  // Note: Preset prompts are automatically initialized on first access
  getPresetPrompts: async (forceRefresh = false) => {
    try {
      const userId = getCurrentUserId();
      
      // Try to load from cache first (unless force refresh is requested)
      if (!forceRefresh && userId) {
        const cachedData = loadFromCache(userId);
        if (cachedData) {
          return cachedData;
        }
      }
      
      const token = localStorage.getItem('dizeeToken');
      console.log('📥 Fetching preset prompts from database...');
      const response = await getDataAPI('user/preset-prompts', token);
      
      console.log('Preset prompts response:', response.data);
      console.log('Preset prompts array:', response.data?.data?.presetPrompts);
      console.log('Number of preset prompts:', response.data?.data?.presetPrompts?.length || 0);
      
      const prompts = response.data?.data?.presetPrompts || [];
      
      // Save to cache
      if (userId && prompts.length > 0) {
        saveToCache(prompts, userId);
        console.log('💾 Saved preset prompts to cache');
      }
      
      return prompts;
    } catch (error) {
      console.error('❌ Error fetching preset prompts:', error);
      throw error;
    }
  },

  // Clear cache (useful when user logs out or prompts are updated)
  clearCache: () => {
    memoryCache = { data: null, timestamp: null, userId: null };
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('🗑️ Cleared preset prompts cache');
    } catch (error) {
      console.warn('Failed to clear preset prompts cache:', error);
    }
  },

  // Toggle a preset prompt on/off for the user
  togglePresetPrompt: async (presetPromptId, enabled) => {
    try {
      console.log('🔄 Toggling preset prompt:', presetPromptId, 'to:', enabled);
      const token = localStorage.getItem('dizeeToken');
      
      const response = await postDataAPI('user/preset-prompts/toggle', {
        presetPromptId,
        enabled
      }, token);
      
      console.log('✅ Toggle response:', response.data);
      
      // Clear cache since prompts have been updated
      presetPromptsApi.clearCache();
      console.log('🗑️ Cleared preset prompts cache after toggle');
      
      return response.data?.data?.userPreference;
    } catch (error) {
      console.error('❌ Error toggling preset prompt:', error);
      throw error;
    }
  }

}; 